
import Vue from 'vue';
import Component from 'vue-class-component';
import { Action, State } from 'vuex-class';
import { Prop, Watch } from 'vue-property-decorator';
import TagsEditor from '@/components/editor/SectionEditor/TagsEditor.vue';
import { RootState } from '@/store/store';
import { TagEntityTypeEnum } from '@/jbi-shared/types/document.types';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { clone } from '@/jbi-shared/util/group-level-attributes.util';
import { deepEqual } from '@/jbi-shared/util/deep-copy.util';
import { DirtyTagMap } from '@/store/modules/documents/types/documents.types';

@Component({
  components: {
    TagsEditor
  }
})
export default class PendingDocumentSettingsForm extends Vue {
  @Prop({ required: true }) documentId!: number; // This is PendingDocumentEntity.id
  @Prop({ default: '250px' }) containerMinHeight!: string;

  public dirtyTagMaps: DirtyTagMap[] = [];
  public originalTags: DirtyTagMap[] = [];
  public projectDocumentFkId: number | undefined | null = undefined;
  public settingsLoaded: boolean = false;

  @State(
    (state: RootState) =>
      state.pendingDocuments.apiState.updateDocumentSettings.loading
  )
  public updating!: boolean;

  @Action('pendingDocuments/getDocumentSettings')
  public getDocumentSettings!: (payload: {
    documentId: number;
  }) => Promise<any>;

  @Action('pendingDocuments/updateDocumentSettings')
  public updateDocumentSettings!: (payload: {
    documentId: number;
    settings: {
      tags?: DirtyTagMap[];
    };
  }) => Promise<void>;

  get hasChanges(): boolean {
    return !deepEqual(this.originalTags, this.dirtyTagMaps);
  }

  get TagEntityTypeEnum() {
    return TagEntityTypeEnum;
  }

  get tagsEditorEntityId(): number {
    if (this.projectDocumentFkId) {
      return this.projectDocumentFkId;
    }
    return this.documentId;
  }

  get isTagsEditorReady(): boolean {
    return this.settingsLoaded;
  }

  /**
   * Load document settings when component is created
   */
  public async created() {
    try {
      const settings = await this.getDocumentSettings({
        documentId: this.documentId
      });

      if (settings) {
        this.dirtyTagMaps = settings.tags || [];
        this.originalTags = clone(settings.tags) || [];
        this.projectDocumentFkId =
          settings.projectDocumentId === null
            ? undefined
            : settings.projectDocumentId;
      }
    } catch (error) {
      this.$buefy.toast.open({
        message: 'Failed to load document settings',
        type: 'is-danger'
      });
      this.dirtyTagMaps = [];
      this.originalTags = [];
      this.projectDocumentFkId = undefined;
    } finally {
      this.settingsLoaded = true;
    }
  }

  /**
   * Save changes to document settings
   */
  public async handleSave() {
    try {
      await this.updateDocumentSettings({
        documentId: this.documentId,
        settings: {
          tags: this.dirtyTagMaps
        }
      });

      this.originalTags = clone(this.dirtyTagMaps);

      this.$buefy.toast.open({
        message: 'Document settings saved',
        position: 'is-top',
        type: 'is-dark'
      });

      this.$emit('update:tags', this.dirtyTagMaps);
      this.$emit('close');
    } catch (error) {
      this.$buefy.toast.open({
        message: 'Failed to save document settings',
        type: 'is-danger',
        position: 'is-top'
      });
    }
  }
}
